// BatchForm — paste many lines, parse + edit list, bulk submit. The
// parser is line-based so the live preview reflects every keystroke;
// per-row "Skip / Restore" toggles let users drop lines without
// editing the source text.

import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Button,
  Empty,
  List,
  Select,
  Tag,
  Textarea,
  type SelectOption,
} from '@viquard/doc-ui/components';
import { IcnAlert } from '@viquard/doc-ui/icons';
import type { CalendarGroup, CalendarKindMap } from '../../types.js';
import {
  formatMessage,
  useCalendarMessages,
} from '../../i18n.js';
import {
  instantFromZonedWallClock,
  parseISODate,
} from '../../utils/dateUtils.js';
import {
  resolveKindDefaults,
  type CalendarKindBehaviorMap,
} from '../../utils/kindBehavior.js';
import { parseBatch, type BatchParseResult } from '../../utils/batchParse.js';
import {
  KIND_DEFAULT_REMINDER_VALUE,
  buildReminderOptions,
  type DefaultReminderSelection,
} from './helpers.js';
import {
  buildDefaultGroupOptions,
  PRIVATE_GROUP_SENTINEL,
} from './group-helpers.js';
import { FooterActions, Section } from './layout.js';
import type { EventComposerSubmitDraft } from './types.js';

export interface BatchFormProps {
  readonly kindTheme: CalendarKindMap;
  readonly kindBehavior: CalendarKindBehaviorMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly onSubmitBatch: (rows: readonly EventComposerSubmitDraft[]) => void;
  readonly onCancel: () => void;
  readonly mobile: boolean;
  readonly viewerTz?: string | undefined;
  readonly recurrenceEnabled: boolean;
  readonly remindersEnabled: boolean;
}

export function BatchForm(props: BatchFormProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    kindTheme,
    kindBehavior,
    groups,
    onSubmitBatch,
    onCancel,
    mobile,
    viewerTz,
    recurrenceEnabled,
    remindersEnabled,
  } = props;
  // Initial text is empty — the example lines render via the textarea
  // placeholder so a stray click on Add can't submit the placeholder
  // events. Users see the same hint, but only their own input parses.
  const [text, setText] = useState('');
  // Default to "no group" so a paste-batch is private until the user
  // explicitly picks one — matches SingleForm's posture and keeps the
  // safer-default invariant uniform across the three modes.
  const [defaultGroupId, setDefaultGroupId] = useState<string | null>(null);
  // Batch rows do not expose per-row reminder editing; this default is
  // stamped onto every parsed draft. `null` intentionally means off.
  const [defaultReminderMinutes, setDefaultReminderMinutes] =
    useState<DefaultReminderSelection>(KIND_DEFAULT_REMINDER_VALUE);
  // Per-row deselect — the parser is line-based and fast, but users
  // sometimes paste a list and want to drop a couple of rows without
  // editing the source text. We track removed-row keys (date+time+title
  // identity) so editing the textarea above doesn't reset the deselect
  // unless the row itself disappears from the parse.
  const [removedKeys, setRemovedKeys] = useState<ReadonlySet<string>>(new Set());

  const kindKeys = useMemo(
    () => Object.keys(kindTheme).filter((k) => k !== 'default'),
    [kindTheme],
  );

  const parsed = useMemo<readonly BatchParseResult[]>(
    () => parseBatch(text, { defaultGroupId, kindKeys, kindBehavior }),
    [text, defaultGroupId, kindKeys, kindBehavior],
  );

  const rowKey = useCallback(
    (p: Extract<BatchParseResult, { error: false }>): string =>
      `${p.date}T${p.time}|${p.kind}|${p.title}`,
    [],
  );

  const valid = parsed.filter(
    (p): p is Extract<BatchParseResult, { error: false }> => !p.error,
  );
  const errors = parsed.filter((p) => p.error);
  const kept = valid.filter((p) => !removedKeys.has(rowKey(p)));

  const submit = (): void => {
    const rows: EventComposerSubmitDraft[] = kept.map((p) => {
      return parsedRowToDraft(
        p,
        viewerTz,
        recurrenceEnabled,
        remindersEnabled ? defaultReminderMinutes : undefined,
        kindBehavior,
      );
    });
    onSubmitBatch(rows);
  };

  const clearAll = (): void => {
    setText('');
    setRemovedKeys(new Set());
  };

  const groupOptions: readonly SelectOption[] = useMemo(
    () => buildDefaultGroupOptions(groups, messages.composerPrimaryGroupNone),
    [groups, messages.composerPrimaryGroupNone],
  );
  const reminderOptions: readonly SelectOption[] = useMemo(
    () => buildReminderOptions(messages, { includeKindDefault: true }),
    [messages],
  );

  return (
    <>
      <div className="vq-cal__composer-scroll">
        <div className="vq-cal__composer-workspace">
          <div className="vq-cal__composer-workspace-form">
            <Section title={messages.composerSectionPaste}>
              <Textarea
                value={text}
                onChange={(e) => { setText(e.target.value); }}
                rows={mobile ? 6 : 8}
                placeholder={messages.composerBatchPlaceholder}
              />
              <div className="vq-cal__composer-batch-bar">
                {/* `groupOptions` always carries the "Private" sentinel, so
                    the picker shows even when the user has no groups —
                    consistent affordance across modes. The sentinel maps
                    back to a literal `null` groupId, matching SingleForm. */}
                <span className="vq-cal__composer-batch-default">
                  <span className="vq-cal__composer-batch-default-label">
                    {messages.composerDefaultGroupLabel}
                  </span>
                  <Select
                    options={groupOptions}
                    value={defaultGroupId ?? PRIVATE_GROUP_SENTINEL}
                    onValueChange={(v) => {
                      setDefaultGroupId(v === PRIVATE_GROUP_SENTINEL ? null : v);
                    }}
                    size="sm"
                  />
                </span>
                {remindersEnabled && (
                  <span className="vq-cal__composer-batch-default">
                    <span className="vq-cal__composer-batch-default-label">
                      {messages.composerDefaultReminderLabel}
                    </span>
                    <Select
                      options={reminderOptions}
                      value={
                        defaultReminderMinutes === KIND_DEFAULT_REMINDER_VALUE
                          ? KIND_DEFAULT_REMINDER_VALUE
                          : defaultReminderMinutes === null
                          ? 'none'
                          : String(defaultReminderMinutes)
                      }
                      onValueChange={(value) => {
                        setDefaultReminderMinutes(
                          value === KIND_DEFAULT_REMINDER_VALUE
                            ? KIND_DEFAULT_REMINDER_VALUE
                            : value === 'none'
                              ? null
                              : Number(value),
                        );
                      }}
                      size="sm"
                    />
                  </span>
                )}
                <span className="vq-cal__composer-batch-stats">
                  <Tag tone="success" size="sm">
                    {formatMessage(messages.composerBatchParsedTag, { count: kept.length })}
                  </Tag>
                  {errors.length > 0 && (
                    <Tag tone="warning" size="sm">
                      {formatMessage(messages.composerBatchUnclearTag, { count: errors.length })}
                    </Tag>
                  )}
                  {text.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      shape="pill"
                      onClick={clearAll}
                    >
                      {messages.composerBatchClear}
                    </Button>
                  )}
                </span>
              </div>
            </Section>
          </div>

          <aside className="vq-cal__composer-preview" aria-live="polite">
            <header className="vq-cal__composer-preview-head">
              <span>{messages.composerSectionPreview}</span>
            </header>
            {parsed.length === 0 ? (
              <Empty
                size="compact"
                title={messages.composerBatchEmptyTitle}
                description={messages.composerBatchEmptyDesc}
              />
            ) : (
              <List size="sm" bordered={false} aria-label={messages.composerParsedListAriaLabel}>
                {parsed.map((p, i) => {
                  if (p.error) {
                    return <BatchErrorRow key={i} source={p.source} message={p.message} />;
                  }
                  const key = rowKey(p);
                  const isRemoved = removedKeys.has(key);
                  return (
                    <BatchValidRow
                      key={i}
                      row={p}
                      kindTheme={kindTheme}
                      removed={isRemoved}
                      onToggleRemove={() =>
                        { setRemovedKeys((prev) => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        }); }
                      }
                      skipLabel={messages.composerSkip}
                      restoreLabel={messages.composerRestore}
                      repeatLabel={messages.composerAiRepeatsTag}
                      allDayLabel={messages.composerDurationAllDay}
                      recurrenceEnabled={recurrenceEnabled}
                    />
                  );
                })}
              </List>
            )}
          </aside>
        </div>
      </div>

      <FooterActions
        primary={
          <Button
            variant="primary"
            size="md"
            disabled={kept.length === 0}
            onClick={submit}
          >
            {kept.length === 0
              ? messages.composerAddEventsZero
              : formatMessage(
                  kept.length === 1
                    ? messages.composerAddEventsOne
                    : messages.composerAddEventsOther,
                  { count: kept.length },
                )}
          </Button>
        }
        secondary={
          <Button variant="ghost" size="md" onClick={onCancel}>
            {messages.composerCancel}
          </Button>
        }
      />
    </>
  );
}

/** Parsed-row → submit draft. Resolves wall-clock to absolute UTC in
 *  viewerTz; all-day rows emit a date-only `startsAt` (TZ-neutral). */
function parsedRowToDraft(
  p: Extract<BatchParseResult, { error: false }>,
  viewerTz: string | undefined,
  recurrenceEnabled: boolean,
  reminderMinutesBefore: DefaultReminderSelection | undefined,
  kindBehavior: CalendarKindBehaviorMap,
): EventComposerSubmitDraft {
  const pd = parseISODate(p.date);
  const defaults = resolveKindDefaults(kindBehavior, p.kind);
  let startsAt: string;
  let endsAt: string | null = null;
  if (p.allDay) {
    startsAt = p.date;
  } else {
    const [hh, mm] = p.time.split(':').map(Number);
    const startMs = instantFromZonedWallClock(
      pd.year,
      pd.month,
      pd.day,
      hh ?? 9,
      mm ?? 0,
      viewerTz,
    );
    startsAt = new Date(startMs).toISOString();
    if (p.endTime !== null) {
      const [eh, em] = p.endTime.split(':').map(Number);
      endsAt = new Date(
        instantFromZonedWallClock(
          pd.year,
          pd.month,
          pd.day,
          eh ?? 9,
          em ?? 0,
          viewerTz,
        ),
      ).toISOString();
    } else if (p.durationMinutes !== null && p.durationMinutes > 0) {
      endsAt = new Date(startMs + p.durationMinutes * 60_000).toISOString();
    } else if (defaults.durationMinutes > 0) {
      endsAt = new Date(startMs + defaults.durationMinutes * 60_000).toISOString();
    }
  }
  const reminder =
    reminderMinutesBefore === KIND_DEFAULT_REMINDER_VALUE
      ? defaults.reminderMinutesBefore
      : reminderMinutesBefore;
  return {
    title: p.title,
    kind: p.kind,
    startsAt,
    endsAt,
    allDay: p.allDay,
    groupId: p.groupId,
    crossPostedTo: [],
    meta: '',
    recurring: recurrenceEnabled && p.recurrence !== null,
    recurrence: recurrenceEnabled ? p.recurrence : null,
    ...(reminder !== undefined ? { reminderMinutesBefore: reminder } : {}),
    link: null,
  };
}

function formatRowTimeRange(row: Extract<BatchParseResult, { error: false }>): string {
  if (row.allDay) return row.date;
  if (row.endTime !== null) {
    return `${row.date} · ${row.time}–${row.endTime}`;
  }
  if (row.durationMinutes !== null && row.durationMinutes > 0) {
    return `${row.date} · ${row.time} (${String(row.durationMinutes)}m)`;
  }
  return `${row.date} · ${row.time}`;
}

function BatchValidRow(props: {
  row: Extract<BatchParseResult, { error: false }>;
  kindTheme: CalendarKindMap;
  removed: boolean;
  onToggleRemove: () => void;
  skipLabel: string;
  restoreLabel: string;
  repeatLabel: string;
  allDayLabel: string;
  recurrenceEnabled: boolean;
}): ReactNode {
  const {
    row,
    kindTheme,
    removed,
    onToggleRemove,
    skipLabel,
    restoreLabel,
    repeatLabel,
    allDayLabel,
    recurrenceEnabled,
  } = props;
  const def = kindTheme[row.kind] ?? kindTheme.default;
  const color = def?.color ?? 'var(--vq-rule-strong)';
  return (
    <List.Item
      leading={
        <span
          className="vq-cal__composer-batch-bar-color"
          style={{ background: color, opacity: removed ? 0.3 : 1 }}
          aria-hidden="true"
        />
      }
      title={
        <span style={removed ? { textDecoration: 'line-through', opacity: 0.55 } : undefined}>
          {row.title}
        </span>
      }
      description={
        <span className="vq-cal__composer-batch-row-meta">
          <Tag tone="neutral" size="sm">
            {formatRowTimeRange(row)}
          </Tag>
          <span
            className="vq-cal__composer-batch-row-kind"
            style={{ color }}
          >
            {def?.label ?? row.kind}
          </span>
          {row.allDay && (
            <Tag tone="neutral" size="sm">
              {allDayLabel}
            </Tag>
          )}
          {recurrenceEnabled && row.recurrence !== null && (
            <Tag tone="accent" size="sm">
              {repeatLabel}
            </Tag>
          )}
        </span>
      }
      trailing={
        <Button
          variant="ghost"
          size="sm"
          shape="pill"
          onClick={onToggleRemove}
        >
          {removed ? restoreLabel : skipLabel}
        </Button>
      }
    />
  );
}

function BatchErrorRow(props: { source: string; message: string }): ReactNode {
  return (
    <List.Item
      leading={
        <span className="vq-cal__composer-batch-error-icon" aria-hidden="true">
          <IcnAlert size={14} />
        </span>
      }
      title={
        <Tag tone="danger" size="sm">
          {props.message}
        </Tag>
      }
      description={
        <span className="vq-cal__composer-batch-error-source">"{props.source}"</span>
      }
    />
  );
}
